import React, { useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { LoanPayment } from "../../services/loanCalculator";
import { formatCurrency } from "../../utils/namesFormater";
import { InterestType, RepaymentType, LoanDetails } from "../../interfaces/DTO/mislaka_xml_models";

interface Props {
  payments: LoanPayment[];
  startDate?: string; // תאריך התחלה בפורמט ISO (YYYY-MM-DD)
  endDate?: string; // תאריך סיום בפורמט ISO (YYYY-MM-DD)
  interestType?: InterestType;
  interestRate?: number; // שיעור הריבית
  isExistingLoan?: boolean; // האם זו הלוואה קיימת
  repaymentType?: RepaymentType; // סוג החזר - שפיצר או ריבית שוטפת
  loanDetails?: LoanDetails; // פרטי ההלוואה המלאים
  reportedPeriod?: string; // תקופה מדווחת (ISO date)
}

const LoanPaymentsTable: React.FC<Props> = ({
  payments,
  startDate,
  endDate,
  interestType,
  interestRate,
  isExistingLoan,
  repaymentType,
  loanDetails,
  reportedPeriod
}) => {
  // בדיקה אם זו הלוואת בלון (ריבית שוטפת)
  const isBalloonLoan = repaymentType === RepaymentType.CURRENT_INTEREST;

  // חישוב האינדקס של reportedPeriod (השורה עם התקופה המדווחת)
  const reportedPeriodIndex = useMemo(() => {
    if (!reportedPeriod || !startDate) {
      return -1; // אין תקופה מדווחת
    }

    // Parse ISO date format: YYYY-MM-DD
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [reportedYear, reportedMonth] = reportedPeriod.split('-').map(Number);

    const monthsDiff = (reportedYear - startYear) * 12 + (reportedMonth - startMonth);
    return monthsDiff; // אינדקס מבוסס 0
  }, [reportedPeriod, startDate]);

  // חישוב האינדקס של endDate (החודש המדווח האחרון)
  const reportedMonthIndex = useMemo(() => {
    if (!isExistingLoan || !startDate || !endDate || interestType !== InterestType.VARIABLE) {
      return payments.length; // כל התשלומים מדווחים
    }

    // Parse ISO date format: YYYY-MM-DD
    const [startYear, startMonth] = startDate.split('-').map(Number);
    const [endYear, endMonth] = endDate.split('-').map(Number);

    const monthsDiff = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
    return monthsDiff;
  }, [isExistingLoan, startDate, endDate, interestType, payments.length]);
  // יצירת תאריכים לכל חודש
  const monthDates = useMemo(() => {
    return payments.map((p, index) => {
      if (startDate && endDate) {
        // Parse ISO date format: YYYY-MM-DD
        const [year, month] = startDate.split('-').map(Number);
        // תמיד נתחיל מיום 1 של החודש
        const start = new Date(year, month - 1, 1); // month-1 כי JS Date מתחיל מ-0

        // הוספת חודשים
        const currentDate = new Date(start);
        currentDate.setMonth(currentDate.getMonth() + index);

        // פורמט MM-YYYY
        const mm = String(currentDate.getMonth() + 1).padStart(2, "0");
        const yyyy = currentDate.getFullYear();
        return `${mm}-${yyyy}`;
      } else {
        // אם אין תאריכים, נתחיל מהיום
        const today = new Date();
        const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        firstOfMonth.setMonth(firstOfMonth.getMonth() + index);
        const mm = String(firstOfMonth.getMonth() + 1).padStart(2, "0");
        const yyyy = firstOfMonth.getFullYear();
        return `${mm}-${yyyy}`;
      }
    });
  }, [payments, startDate, endDate]);
  const totals = useMemo(() => {
    const mainTotals = payments.reduce(
      (acc, payment) => ({
        totalPayment: acc.totalPayment + payment.totalPayment,
        principalPayment: acc.principalPayment + payment.principalPayment,
        interestPayment: acc.interestPayment + payment.interestPayment,
      }),
      { totalPayment: 0, principalPayment: 0, interestPayment: 0 }
    );

    return { main: mainTotals };
  }, [payments]);

  return (
    <Box sx={{ mt: 4 }}>
      <Typography variant="h5" sx={{ mb: 2, fontWeight: "bold" }}>
        החזרים צפויים (לוח סילוקין) - לפי ריבית {interestRate ? `${interestRate}%` : ''}
      </Typography>
      {interestType === InterestType.VARIABLE && (
        <Typography variant="body2" sx={{ mb: 2, color: "warning.main", fontStyle: "italic" }}>
          *הריבית משתנה ולכן מדובר בהערכה בלבד לפי הריבית הנוכחית
        </Typography>
      )}

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white", fontWeight: "bold", width: 80 }}>#</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>חודש</TableCell>
              <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                {isBalloonLoan ? "תשלום ריבית" : "תשלום כולל"}
              </TableCell>
              {!isBalloonLoan && (
                <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                  תשלום קרן
                </TableCell>
              )}
              {!isBalloonLoan && (
                <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                  תשלום ריבית
                </TableCell>
              )}
              <TableCell align="right" sx={{ color: "white", fontWeight: "bold" }}>
                יתרת חוב
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(isBalloonLoan ? payments.slice(0, -1) : payments).map((payment, index) => {
              const isFuturePayment = index >= reportedMonthIndex;
              const isEmpty = isFuturePayment && isExistingLoan && interestType === InterestType.VARIABLE;
              const isReportedPeriod = index === reportedPeriodIndex;

              return (
                <TableRow
                  key={payment.month}
                  sx={{
                    "&:nth-of-type(odd)": { backgroundColor: "action.hover" },
                    "&:hover": { backgroundColor: "action.selected" },
                    opacity: isEmpty ? 0.5 : 1,
                  }}
                >
                  <TableCell sx={{ fontWeight: "bold", color: "text.secondary" }}>
                    {isReportedPeriod && <span style={{ color: "green", marginLeft: "4px" }}>◀</span>}
                    {index + 1}
                  </TableCell>
                  <TableCell>{monthDates[index]}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {isEmpty ? "-" : formatCurrency(payment.totalPayment)}
                    {isFuturePayment && !isEmpty && " *"}
                  </TableCell>
                  {!isBalloonLoan && (
                    <TableCell align="right" sx={{ color: "success.main" }}>
                      {isEmpty ? "-" : formatCurrency(payment.principalPayment)}
                      {isFuturePayment && !isEmpty && " *"}
                    </TableCell>
                  )}
                  {!isBalloonLoan && (
                    <TableCell align="right" sx={{ color: "warning.main" }}>
                      {isEmpty ? "-" : formatCurrency(payment.interestPayment)}
                      {isFuturePayment && !isEmpty && " *"}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    {isEmpty ? "-" : formatCurrency(payment.remainingBalance)}
                    {isFuturePayment && !isEmpty && " *"}
                    {isReportedPeriod && loanDetails?.loan_balance !== undefined && (
                      <Typography component="span" sx={{ color: "warning.main", fontSize: "0.85rem", mr: 1 }}>
                        ({formatCurrency(loanDetails.loan_balance)})
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {!isBalloonLoan && (
              <TableRow sx={{ backgroundColor: "primary.light" }}>
                <TableCell colSpan={2} sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>סה"כ</TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  {formatCurrency(totals.main.totalPayment)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "success.main" }}>
                  {formatCurrency(totals.main.principalPayment)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "warning.main" }}>
                  {formatCurrency(totals.main.interestPayment)}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: "bold", fontSize: "1.1rem" }}>
                  ₪0
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {isExistingLoan && interestType === InterestType.VARIABLE && reportedMonthIndex < payments.length && (
        <Box sx={{ mt: 2, p: 2, bgcolor: "info.light", borderRadius: 1 }}>
          <Typography variant="body2" sx={{ color: "info.dark" }}>
            * תשלומים עתידיים מחושבים תחת ההנחה שהריבית תשאר זהה. התשלומים בפועל עשויים להשתנות בהתאם לשינויי הריבית.
          </Typography>
        </Box>
      )}

      {isBalloonLoan && payments.length > 0 && (
        <Box sx={{ mt: 3, p: 3, bgcolor: "success.light", borderRadius: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2, color: "success.dark" }}>
            תשלום קרן בסוף התקופה
          </Typography>
          <Box sx={{ display: "flex", gap: 4, alignItems: "center" }}>
            <Box>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                תאריך תשלום
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold", color: "success.dark" }}>
                {monthDates[monthDates.length - 1]}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                סכום לתשלום
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold", color: "success.dark" }}>
                {formatCurrency(payments[payments.length - 1].principalPayment)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                סה"כ ריבית מוערכת לתשלום
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: "bold", color: "warning.dark" }}>
                {formatCurrency(totals.main.interestPayment)}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default LoanPaymentsTable;
